import {Box} from "@mui/material";
import {Theme, useTheme} from "@mui/material/styles";
import {SystemStyleObject} from "@mui/system";
import React, {useState} from "react";
import {signingPanel} from "./signingColors";
import {FooterInkProvider} from "./SigningFooter";
import SigningHeader, {SigningLogo, useSigningLogo} from "./SigningHeader";
import {HERO_BANNER_SX, photoOpacity} from "./signingPhoto";
import {RowToneProvider} from "./SigningRows";
import {cardRadius, SIGNING_SURFACE, panelColorsOf} from "./signingTheme";
import {DigitalSigningVariant, SnapshotHotel, SnapshotTheme} from "./digitalSigning.types";

const COLUMN_WIDTH = 600;
const CARD_OVERLAP = 40;
const pageEnd = (fill?: boolean) => (fill ? 'calc(48px + env(safe-area-inset-bottom, 0px))' : 6);
const FILL_SCREEN = {minHeight: '100vh', '@supports (min-height: 100dvh)': {minHeight: '100dvh'}};

interface Props {
    theme?: SnapshotTheme;
    hotel?: SnapshotHotel;
    head?: React.ReactNode;
    body: React.ReactNode;
    footer?: React.ReactNode;
    fill?: boolean;
}

export const variantOf = (theme?: SnapshotTheme): DigitalSigningVariant => {
    if (theme?.variant === 'HERO' || theme?.variant === 'SPLIT') return 'HERO';
    return theme?.variant === 'BACKGROUND' ? 'BACKGROUND' : 'PAPER';
};

const Column = ({children}: {children: React.ReactNode}) => (
    <Box sx={{width: '100%', maxWidth: COLUMN_WIDTH, mx: 'auto'}}>{children}</Box>
);

// Hero's and Background's white card
const cardSx = (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'relative',
    bgcolor: SIGNING_SURFACE,
    color: 'text.primary',
    borderRadius: `${cardRadius(theme)}px`,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06), 0 16px 40px -20px rgba(0, 0, 0, 0.35)',
    px: {xs: 2.5, sm: 4},
    py: {xs: 3, sm: 4},
    width: 'calc(100% - 32px)',
    maxWidth: COLUMN_WIDTH + 64,
    mx: 'auto',
});

function PaperShell({theme, hotel, head, body, footer, fill}: Props) {
    return (
        <Box sx={{px: {xs: 2, sm: 3}, pt: {xs: 4, sm: 6}, pb: pageEnd(fill)}}>
            <Column>
                <SigningHeader theme={theme} hotel={hotel}/>
                {!!head && <Box sx={{mb: {xs: 4, sm: 5}}}>{head}</Box>}
                {body}
                {!!footer && (
                    <Box sx={{mt: {xs: 5, sm: 6}, pt: 1, borderTop: '1px solid', borderColor: 'divider', '&:empty': {display: 'none'}}}>
                        {footer}
                    </Box>
                )}
            </Column>
        </Box>
    );
}

/** The design's photo over its box, centered; it fades in once loaded and shows nothing when broken. */
function ShellPhoto({src, opacity = 1}: {src: string, opacity?: number}) {
    const [loaded, setLoaded] = useState(false);

    return (
        <Box
            component={'img'}
            src={src}
            alt={''}
            onLoad={() => setLoaded(true)}
            sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: loaded ? opacity : 0,
                transition: 'opacity 400ms cubic-bezier(0.22, 1, 0.36, 1)',
                '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
                '@media print': {opacity, transition: 'none'},
            }}
        />
    );
}

function HeroShell({theme, hotel, head, body, footer, fill}: Props) {
    const muiTheme = useTheme();
    const panel = signingPanel(muiTheme);
    const heroUrl = theme?.heroUrl;
    const logo = useSigningLogo({theme, hotel});

    return (
        <Box sx={fill ? FILL_SCREEN : undefined}>
            <Box sx={{position: 'relative', bgcolor: panel.background, color: panel.text, overflow: 'hidden'}}>
                {/* The photo is the page's header: no logo on it, whose ink no photo can promise to carry */}
                {!!heroUrl && (
                    <Box sx={{position: 'relative', overflow: 'hidden', borderRadius: {xs: 0, sm: `${cardRadius(muiTheme)}px`}, ...HERO_BANNER_SX}}>
                        <ShellPhoto key={heroUrl} src={heroUrl}/>
                    </Box>
                )}

                <Box sx={{position: 'relative', px: {xs: 2, sm: 3}, pt: heroUrl ? {xs: 3, sm: 4} : {xs: 4, sm: 6}, pb: `${CARD_OVERLAP + 32}px`}}>
                    <Column>
                        {!heroUrl && !!logo && (
                            <Box sx={{mb: {xs: 3, sm: 4}}}>
                                <SigningLogo logo={logo} hotel={hotel} align={'left'}/>
                            </Box>
                        )}
                        <RowToneProvider value={{label: panel.textSecondary, rule: panel.rule}}>
                            {head}
                        </RowToneProvider>
                    </Column>
                </Box>
            </Box>

            <Box sx={[cardSx(muiTheme), {mt: `-${CARD_OVERLAP}px`, mb: footer ? 0 : pageEnd(fill)}]}>
                {body}
            </Box>

            {!!footer && (
                <Box sx={{px: {xs: 2, sm: 3}, pb: pageEnd(fill)}}>
                    <Column>
                        <Box sx={{pt: {xs: 2, sm: 3}, '&:empty': {display: 'none'}}}>{footer}</Box>
                    </Column>
                </Box>
            )}
        </Box>
    );
}

/**
 * Background's photo under its veil, behind the page: on the guest's page a fixed layer as large as
 * the screen - not background-attachment: fixed, which iOS Safari ignores, and the large viewport, so
 * the photo doesn't jump when Safari's bars collapse -, elsewhere (the signed page's dialog) its
 * parent's area. The veil is the layer's color, the photo shows through it at the design's opacity.
 * Not printed.
 */
function BackgroundLayer({theme, fill}: {theme?: SnapshotTheme, fill?: boolean}) {
    const heroUrl = theme?.heroUrl;

    return (
        <Box
            aria-hidden
            data-testid={'signing-background'}
            sx={{
                ...(fill
                    ? {position: 'fixed', top: 0, left: 0, right: 0, height: '100vh', '@supports (height: 100lvh)': {height: '100lvh'}}
                    : {position: 'absolute', inset: 0}),
                zIndex: 0,
                overflow: 'hidden',
                bgcolor: panelColorsOf(theme).background,
                pointerEvents: 'none',
                '@media print': {display: 'none'},
            }}
        >
            {!!heroUrl && <ShellPhoto key={heroUrl} src={heroUrl} opacity={photoOpacity(theme)}/>}
        </Box>
    );
}

function BackgroundShell({theme, hotel, head, body, footer, fill}: Props) {
    const muiTheme = useTheme();
    // The panel color, else the paper (see signingTheme)
    const veil = signingPanel(muiTheme);
    const logo = useSigningLogo({theme, hotel});

    return (
        <Box sx={{position: 'relative', isolation: 'isolate', ...(fill ? FILL_SCREEN : {})}}>
            <BackgroundLayer theme={theme} fill={fill}/>

            <Box sx={{position: 'relative', zIndex: 1, pt: {xs: 4, sm: 6}, pb: pageEnd(fill)}}>
                {!!logo && (
                    <Box sx={{px: {xs: 2, sm: 3}, mb: {xs: 3, sm: 4}}}>
                        <SigningLogo logo={logo} hotel={hotel}/>
                    </Box>
                )}

                {/* Printed without the photo and the veil, it reads like Paper: no shadow on the paper */}
                <Box sx={[cardSx(muiTheme), {'@media print': {boxShadow: 'none'}}]}>
                    {!!head && <Box sx={{mb: {xs: 4, sm: 5}}}>{head}</Box>}
                    {body}
                </Box>

                {!!footer && (
                    <Box sx={{px: {xs: 2, sm: 3}}}>
                        <Column>
                            <FooterInkProvider value={veil.text}>
                                <Box
                                    sx={{
                                        pt: {xs: 2, sm: 3},
                                        '&:empty': {display: 'none'},
                                        // A halo in the veil's color keeps the ink readable where the photo shows through
                                        '@media screen': {textShadow: `0 0 2px ${veil.background}, 0 0 10px ${veil.background}`},
                                    }}
                                >
                                    {footer}
                                </Box>
                            </FooterInkProvider>
                        </Column>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function SigningShell(props: Props) {
    switch (variantOf(props.theme)) {
        case 'HERO':
            return <HeroShell {...props}/>;
        case 'BACKGROUND':
            return <BackgroundShell {...props}/>;
        default:
            return <PaperShell {...props}/>;
    }
}

export default SigningShell;
